/**
 * AUTODOC 평가 모듈 - 공통 UI 컴포넌트
 */
import React from 'react';
import { ORDER_STATUS, INSPECTOR_LEVELS, getPaintColor } from '../utils/constants';

// ─── 상태 뱃지 ───
export function StatusBadge({ status }) {
  const s = ORDER_STATUS[status] || { label: status, color: '#888', bg: '#f0f0f0' };
  return (
    <span style={{
      display: 'inline-block', padding: '3px 10px', borderRadius: 6,
      fontSize: 12, fontWeight: 600, color: s.color, background: s.bg,
      letterSpacing: '-0.3px',
    }}>
      {s.label}
    </span>
  );
}

// ─── 평가사 등급 뱃지 ───
export function LevelBadge({ level }) {
  const cfg = INSPECTOR_LEVELS[level] || INSPECTOR_LEVELS['일반'];
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 5,
      fontSize: 11, fontWeight: 600, color: cfg.color, background: cfg.bg,
    }}>
      {level}
    </span>
  );
}

// ─── 별점 (읽기전용 + 입력용) ───
export function Stars({ value = 0, size = 14, onChange, gap = 2 }) {
  return (
    <span style={{ display: 'inline-flex', gap }}>
      {[1, 2, 3, 4, 5].map(i => (
        <span
          key={i}
          onClick={() => onChange?.(i)}
          style={{
            fontSize: size,
            cursor: onChange ? 'pointer' : 'default',
            color: i <= value ? '#EF9F27' : '#D3D1C7',
            lineHeight: 1,
            transition: 'color 0.15s',
          }}
        >
          ★
        </span>
      ))}
    </span>
  );
}

// ─── 도막 측정 차량 맵 ───
export function PaintMap({ data = {}, width = 200 }) {
  const panels = [
    { k: 'hood',     l: '본넷',   x: 60, y: 8,   w: 80, h: 32 },
    { k: 'fenderL',  l: '좌펜더', x: 5,  y: 46,  w: 48, h: 46 },
    { k: 'fenderR',  l: '우펜더', x: 147, y: 46, w: 48, h: 46 },
    { k: 'roof',     l: '루프',   x: 60, y: 46,  w: 80, h: 166 },
    { k: 'doorFL',   l: '좌앞문', x: 5,  y: 98,  w: 48, h: 54 },
    { k: 'doorFR',   l: '우앞문', x: 147, y: 98, w: 48, h: 54 },
    { k: 'doorRL',   l: '좌뒷문', x: 5,  y: 158, w: 48, h: 54 },
    { k: 'doorRR',   l: '우뒷문', x: 147, y: 158, w: 48, h: 54 },
    { k: 'trunk',    l: '트렁크', x: 60, y: 218, w: 80, h: 32 },
    { k: 'quarterL', l: '좌뒤펜', x: 5,  y: 218, w: 48, h: 32 },
    { k: 'quarterR', l: '우뒤펜', x: 147, y: 218, w: 48, h: 32 },
  ];

  // data 키 매핑 (snake_case ↔ camelCase 호환)
  const getValue = (key) => {
    const map = {
      hood: ['hood', 'paint_hood'],
      fenderL: ['fenderL', 'paint_fender_l'],
      fenderR: ['fenderR', 'paint_fender_r'],
      roof: ['roof', 'paint_roof'],
      doorFL: ['doorFL', 'paint_door_fl'],
      doorFR: ['doorFR', 'paint_door_fr'],
      doorRL: ['doorRL', 'paint_door_rl'],
      doorRR: ['doorRR', 'paint_door_rr'],
      trunk: ['trunk', 'paint_trunk'],
      quarterL: ['quarterL', 'paint_quarter_l'],
      quarterR: ['quarterR', 'paint_quarter_r'],
    };
    for (const k of (map[key] || [key])) {
      if (data[k] !== undefined && data[k] !== null) return data[k];
    }
    return null;
  };

  return (
    <div>
      <svg viewBox="0 0 200 225" width={width} style={{ display: 'block', margin: '0 auto' }}>
        {panels.map(p => {
          const val = getValue(p.k);
          const color = getPaintColor(val);
          return (
            <g key={p.k}>
              <rect x={p.x} y={p.y} width={p.w} height={p.h} rx={4}
                fill={color} opacity={0.2} stroke={color} strokeWidth={1.5} />
              <text x={p.x + p.w / 2} y={p.y + p.h / 2 - 5}
                textAnchor="middle" fontSize={8} fill="#888">{p.l}</text>
              <text x={p.x + p.w / 2} y={p.y + p.h / 2 + 8}
                textAnchor="middle" fontSize={10} fontWeight={600} fill={color}>
                {val || '-'}
              </text>
            </g>
          );
        })}
      </svg>
      {/* 범례 */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginTop: 6 }}>
        {[
          { color: '#1D9E75', label: '~150 정상' },
          { color: '#EF9F27', label: '150~250 도장' },
          { color: '#E24B4A', label: '250+ 판금' },
        ].map(l => (
          <span key={l.label} style={{ fontSize: 10, display: 'flex', alignItems: 'center', gap: 3 }}>
            <span style={{ width: 8, height: 8, borderRadius: 2, background: l.color, display: 'inline-block' }} />
            <span style={{ color: '#888' }}>{l.label}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

// ─── 요약 카드 ───
export function SummaryCard({ label, value, color, bg, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        background: bg || '#f5f5f0',
        borderRadius: 10,
        padding: '10px 12px',
        textAlign: 'center',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'transform 0.1s',
      }}
    >
      <div style={{ fontSize: 11, color: color || '#888', fontWeight: 500 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 700, color: color || '#222', marginTop: 2 }}>{value}</div>
    </div>
  );
}

// ─── 정보 행 (라벨: 값) ───
export function InfoRow({ label, value, highlight }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '6px 0', borderBottom: '1px solid #f0f0ec',
    }}>
      <span style={{ fontSize: 13, color: '#888' }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 500, color: highlight || '#222' }}>{value}</span>
    </div>
  );
}

// ─── 빈 상태 ───
export function EmptyState({ message = '데이터가 없습니다' }) {
  return (
    <div style={{
      textAlign: 'center', padding: '48px 20px',
      color: '#aaa', fontSize: 14,
    }}>
      {message}
    </div>
  );
}
